import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { DebounceInput } from "react-debounce-input";
import Switch from "react-switch";
import { AiOutlineCloseSquare } from "react-icons/ai";
import { GrPrint } from "react-icons/gr";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { Alert as Alert2, Button, Card, CardBody, Col, InputGroup, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, UserContext } from "../utils";
import {
   FilterableScreen, ItemDropdownMenu,
   ItemTypeahead, Pager, ServiceThreadSpinner, SpinnerButton
} from "./";

const pageSize = 25;
const PARTS_LOAD_FAILED_MESSAGE = "The machine parts could not be loaded. If this persists, contact a system admin.";
const NOTHING_TO_PRINT_MESSAGE = "Nothing to print on this page.";
const PRINT_FAILED_MESSAGE ="The labels could not be printed. If this persists, contact a system admin.";
// The screen loads twice on open (after the printer list, and on its page/item effect), so one failure toasts once.
const PARTS_LOAD_TOAST_ID = "parts-manager-load";

export default function PartsManager(props) {
   const { currentUser } = useContext(UserContext);
   const [errorMessage, setErrorMessage] = useState(null);
   const [isPrinting, setIsPrinting] = useState(false);
   const [loading, setLoading] = useState(false);
   const [message, setMessage] = useState(null);
   const [pageNumber, setPageNumber] = useState(1);
   const [paginatedList, setPaginatedList] = useState({
      list: [],
      pageNumber: 1,
      totalPages: 1,
   });
   const [printer, setPrinter] = useState(null);
   const [printerOptions, setPrinterOptions] = useState([]);
   const [printToPrinter, setPrintToPrinter] = useState(true);
   const [refresh, setRefresh] = useState(false);
   const [selectedItem, setSelectedItem] = useState(null);
   const handlePrint = nextChecked => { setPrintToPrinter(nextChecked); }

   useEffect(() => {
      document.title = `Parts Manager`
      let apiCalls = [];
      apiCalls.push(
         api
            .post("Reference/GetPrinters", {
               TypeOfPrinter: constants.PRINTER_TYPES.PARTS.value,
            })
            .then((response) => {
               return {
                  printerList: formatPrinters(response?.data?.printers),
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setPrinterOptions(aggResults.printerList ? aggResults.printerList : []);
         })
         .catch((error) =>
            setMessage({
               flavor: "alert-danger",
               text: "Error loading the printer drop-down list",
            })
         )
         .finally(() => refreshData());
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [pageNumber, selectedItem, refresh]);

   function createPayload() {
      let payload = {
         sortField: null,
         sortDirection: null,
         pageNumber: pageNumber,
         pageSize: pageSize,
      };
      if (selectedItem) {
         payload.ItemNumber = selectedItem.itemNumber;
      }
      return payload;
   }

   function formatPrinters(list) {
      if (!list || !list.length || list.length === 0) return [];
      const valueLabelList = _.chain(list)
         .map((x) => {
            x.label = x.nameOnly;
            x.value = x.id;
            return x;
         })
         .sortBy((x) => {
            return x.label;
         })
         .value();
      return valueLabelList;
   }

   function onChange(field, value, index) {
      let newList = _.assign([], paginatedList.list);
      newList[index][field] = value;
      let changes = _.assign({}, paginatedList);
      changes.list = newList;
      setPaginatedList(changes);
   }

   function onClear(itemNumber) {
      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
      // of the / character so the URL is broken and a Not Found error is thrown.
      var encodedItem = itemNumber.replace(/\//g, '%2F').replace(/#/g, '%23');

      api
         .fetch(`SewingTech/ClearBinLocation/${encodedItem}`)
         .then((response) => {
            if (response) {
               toast.success("Successully cleared bin location.");
               setRefresh(!refresh);
            } else {
               toast.error(`Failed to clear current bin for ${itemNumber}`);
            }
         })
         .catch(api.catchHandler);
   }

   function onPrint(indexList) {
      // One print at a time - a double-click would otherwise send the same labels twice.
      if (isPrinting) return;
      // Print All on an empty page would post no labels.
      if (!indexList.length) {
         toast.warning(NOTHING_TO_PRINT_MESSAGE);
         return;
      }
      let payload = {};
      if (!printer) {
         toast.error("Please pick a printer.");
         return;
      } else {
         payload.PrinterId = printer.value;
         payload.PrintToPrinter = printToPrinter;
      }
      let labels = [];
      let errors = [];
      let notPltwhseItems = [];
      _.forEach(indexList, (index) => {
         let item = paginatedList.list[index];
         if (item.printQuantity !== 0) {
            if (item.siteId !== "PLTWHSE") { // Alert user when item is not at PLTWHSE and bin will not be updated
               notPltwhseItems.push(item.itemNumber);
            }

            if (!item.printQuantity) {
               errors.push(item.itemNumber);
            } else {
               labels.push({
                  ItemNumber: item.itemNumber,
                  PrintQuantity: item.printQuantity,
                  Bin: item.newBin || item.bin,
               });
            }
         }
      });
      if (errors.length) {
         toast.error(`Please enter a print quantity of 1 or more on these items: ${errors.join(", ")}.`);
         return;
      }

      if (notPltwhseItems.length) {
         if (notPltwhseItems.length === 1) {
            onShowAlert(`Item number: ${notPltwhseItems[0]} does not have a location of PLTWHSE and its bin will not be updated.`);
         } else {
            onShowAlert(`One or more items being printed do not have a location of PLTWHSE and will not have their bins updated.`);
         }
      }

      payload.Labels = labels;
      if (printToPrinter) {
         setIsPrinting(true);
         api
            .post("Printer/PrintPartLabels", payload)
            .then((res) => {
               if (res && res.data && res.data.success) {
                  toast.success("Label(s) sent to the printer");
               } else if (!api.isHandledByInterceptor(res)) {
                  toast.warning(res?.data?.message || PRINT_FAILED_MESSAGE);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setIsPrinting(false));
      }
      else {
         setIsPrinting(true);
         api.postDownloadBinary(
            "Printer/PrintPartLabels",
            payload,
            constants.MIME_TYPES.pdf,
            `Part_Labels_${dateHelpers.monthDayAndYear(Date.now())}.pdf`
         )
            .then((response) => {
               if (response?.data === false) {
                  toast.error(response.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setIsPrinting(false));
      }

      refreshData();
   }

   function onPrintReport() {
      setLoading(true);
      api
         .postDownloadBinary(
            "Report/ItemBinReport",
            {},
            constants.MIME_TYPES.pdf,
            `Parts_Inventory_List_${dateHelpers.toMDYDateString(new Date())}.pdf`,
            "There was an error downloading this inventory list pdf"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));
   }

   function onShowAlert(message) {
      setErrorMessage(message);
      setTimeout(() => {
         setErrorMessage(null);
      }, 5000);
   }

   function refreshData() {
      setMessage(null);
      api
         .post("SewingTech/GetPaginatedMachineParts", createPayload())
         .then((response) => {
            // On a failure show no parts rather than the previous page's, which Print would then label.
            if (api.toastFailure(response, PARTS_LOAD_FAILED_MESSAGE, PARTS_LOAD_TOAST_ID)) {
               setPaginatedList({ list: [], pageNumber: 1, totalPages: 1 });
               return;
            }
            setPaginatedList(response.data.message);
         })
         .catch(api.catchHandler);
   }

   const printAllButton = (
      <Button
         // Bounded by the rows actually on the page (the last page or a filtered search has fewer than pageSize).
         onClick={() => onPrint(_.range(paginatedList.list?.length ?? 0))}
         children={"Print All"}
         className="shredButton float-end"
      />
   );

   return (
      <DesktopContainer screenName="Parts Manager">
         {errorMessage && <Alert2 color="danger" children={errorMessage} />}
         <FilterableScreen
            filters={
               <div className="filter-header mb-3">
                  <Row className="justify-content-center pe-2">
                     <Col xs={9}>
                        <Row className="align-items-center my-1">
                           <Col xs={4}>
                              <h5 children="Item #:" className="float-end text-white" />
                           </Col>
                           <Col xs={8}>
                              <ItemTypeahead
                                 onChange={(e) => {
                                    setPageNumber(1);
                                    setSelectedItem(e);
                                 }}
                                 value={selectedItem}
                                 placeholder="Type the item number you are looking for (Leave blank for all)"
                              />
                           </Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col xs={{ offset: 1, size: 3 }}>
                              <h5 children="Printer:" className="float-end text-white" />
                           </Col>
                           <Col xs={8}>
                              <Select
                                 classNamePrefix="serviceThreadSelect"
                                 closeMenuOnSelect={true}
                                 components={makeAnimated()}
                                 options={printerOptions}
                                 onChange={setPrinter}
                                 value={printer}
                              />
                           </Col>
                        </Row>
                        {currentUser.isAdmin ?
                           <Row className="align-items-center my-1">
                              <Col xs={{ offset: 1, size: 3 }}>
                                 <h5 className="float-end text-white">{printToPrinter ? "Print to Printer:" : "Download as PDF:"}</h5>
                              </Col>
                              <Col className="h6 fw-bold" xs={8}>
                                 <Switch onChange={handlePrint} checked={printToPrinter} className="react-switch" />
                              </Col>
                           </Row>
                           : null
                        }
                     </Col>
                     <Col className="pe-4">
                        <Row>
                           <Col>
                              <Button
                                 onClick={() => onPrintReport()}
                                 children={"Print Inventory List"}
                                 className="shredButton float-end mb-2"
                              />
                           </Col>
                        </Row>
                        <Row>
                           <Col>{printAllButton}</Col>
                        </Row>
                     </Col>
                  </Row>
               </div>
            }
            pager={
               <Row>
                  <Col>
                     <div className="float-start">
                        <Pager
                           pageNumber={paginatedList?.pageNumber ? paginatedList.pageNumber : 0}
                           totalPages={paginatedList?.totalPages ? paginatedList.totalPages : 0}
                           callBack={(newPageNumber) => setPageNumber(newPageNumber)}
                        />
                     </div>
                  </Col>
                  <Col>{printAllButton}</Col>
               </Row>
            }
         >
            <ServiceThreadSpinner on={loading}>
               <Card>
                  <CardBody>
                     <Row className="p-0 m-0">
                        <Col xs="12">
                           {message ? (
                              <Alert2 className={message.flavor}>{message.text}</Alert2>
                           ) : null}
                           <Table striped hover size="sm" responsive={false} id="userTable">
                              <thead>
                                 <tr>
                                    <th width="30%">Item #</th>
                                    <th>Description</th>
                                    <th>Current Bin</th>
                                    <th width="12%">New Bin</th>
                                    <th width="15%">Print Quantity</th>
                                    <th>Clear</th>
                                 </tr>
                              </thead>
                              <tbody className="fontSize85">
                                 {paginatedList &&
                                    paginatedList.list &&
                                    paginatedList.list.length > 0
                                    ? _.map(paginatedList.list, (t, index) => (
                                       <tr key={`thread-${t.itemNumber}`}>
                                          <ItemDropdownMenu
                                             itemNumber={t.itemNumber}
                                             itemDescription={t.itemNumber}
                                             attachmentCount={t.itemAttachmentCount}
                                          />
                                          <td>{t.itemDescription}</td>
                                          <td>{t.bin}</td>
                                          <td>
                                             <DebounceInput
                                                value={t.newBin || t.bin}
                                                onChange={(e) => onChange("newBin", e.target.value, index)}
                                                className="form-control height38"
                                             />
                                          </td>
                                          <td>
                                             <InputGroup>
                                                <Select
                                                   classNamePrefix="serviceThreadSelect"
                                                   closeMenuOnSelect={true}
                                                   components={makeAnimated()}
                                                   options={constants.PRINT_QTY_OPTIONS}
                                                   onChange={(selection) => onChange("printQuantity", selection.value, index)}
                                                   value={_.find(
                                                      constants.PRINT_QTY_OPTIONS,
                                                      (opt) => {
                                                         return opt.value === t.printQuantity;
                                                      }
                                                   )}
                                                   placeholder={"#"}
                                                />
                                                <SpinnerButton
                                                   on={isPrinting}
                                                   onClick={() => onPrint([index])}
                                                   children={<GrPrint className="whiteIcon" />}
                                                   className="shredButton"
                                                />
                                             </InputGroup>
                                             {t.strength}
                                          </td>
                                          <td>
                                             <AiOutlineCloseSquare
                                                style={{ cursor: "pointer" }}
                                                onClick={() => onClear(t.itemNumber)}
                                                className="text-danger ms-2 mt-1"
                                                size="1.75em"
                                             />
                                          </td>
                                       </tr>
                                    ))
                                    : null}
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  </CardBody>
               </Card>
            </ServiceThreadSpinner>
         </FilterableScreen>
      </DesktopContainer>
   );
}
